import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {FaIcon} from '../../FaIcon';
import {Buildings, ChatCenteredText, UsersThree} from '../../icons';
import {DashboPromptCard} from './DashboPromptCard';
import {ALL_CLIENTS, groupOwnPrompts, sortClients} from './groupOwnPrompts';
import {PromptRow} from './PromptRow';

const RAW = {interpolation: {escapeValue: false}};
// Cuántos sugeridos de Dashbo ofrece el estado vacío para adaptar sin cambiar de solapa.
const SUGGESTED_COUNT = 3;

function clientLabel(t, client) {
    return client.is_active === false
        ? t('agency_prompts.group_client_inactive', {client: client.name, ...RAW})
        : client.name;
}

/**
 * Sin prompts propios: cómo cargar el primero y, al lado, los primeros sugeridos de Dashbo que la
 * agencia todavía no adaptó, con «Adaptar» ahí mismo. Antes el vacío sólo mandaba a la otra solapa.
 */
function EmptyOwn({agencyName, suggested, dashboTotal, adaptingId, onNew, onSeeDashbo, onAdapt, onSeeAdapted, onView, onRate}) {
    const {t} = useTranslation();
    return (
        <>
            <div className="agp-empty">
                {/* El mismo ícono de la entrada «Prompts» de la barra. */}
                <span className="agp-empty__icon" aria-hidden="true"><ChatCenteredText size={32}/></span>
                <h2 className="agp-empty__title">{t('agency_prompts.empty_title', {agency: agencyName, ...RAW})}</h2>
                <p className="agp-empty__body">{t('agency_prompts.empty_body')}</p>
                <div className="agp-empty__actions">
                    <button type="button" className="agp-btn -primary" onClick={onNew}>
                        <FaIcon iconName="plus"/>{t('agency_prompts.empty_new')}
                    </button>
                </div>
            </div>
            {suggested.length > 0 && (
                <section className="agp-suggested" aria-labelledby="agp-suggested-title">
                    <div className="agp-suggested__head">
                        <h2 id="agp-suggested-title" className="agp-suggested__title">
                            {t('agency_prompts.empty_suggested_title')}
                        </h2>
                        <button type="button" className="agp-btn -link" onClick={onSeeDashbo}>
                            {t('agency_prompts.empty_see_dashbo', {total: dashboTotal})}
                        </button>
                    </div>
                    <div className="agp-cards">
                        {suggested.map((prompt) => (
                            <DashboPromptCard key={prompt.id} prompt={prompt} agencyName={agencyName}
                                              adapting={adaptingId === prompt.id} onAdapt={onAdapt}
                                              onSeeAdapted={onSeeAdapted} onView={onView} onRate={onRate}/>
                        ))}
                    </div>
                </section>
            )}
        </>
    );
}

/**
 * El título de un grupo dice el ALCANCE, no sólo el nombre: «Para todos los clientes» o «Sólo para
 * <cliente>», con la consecuencia al lado (a quién le pregunta la IA, o que la frase ya lleva el
 * cliente). Con «Dashbo Ads · 1» a secas no se leía que el prompt era de un solo cliente.
 */
function GroupHeading({group}) {
    const {t} = useTranslation();
    const label = group.client
        ? t('agency_prompts.group_client_only', {
            client: group.client.isActive
                ? group.client.name
                : t('agency_prompts.group_client_inactive', {client: group.client.name, ...RAW}),
            ...RAW,
        })
        : t('agency_prompts.group_all_clients');
    return (
        <div className="agp-group__head">
            <h3 className="agp-group__title">
                {group.client
                    ? <Buildings size={15} weight="bold" aria-hidden="true"/>
                    : <UsersThree size={15} weight="bold" aria-hidden="true"/>}
                <span>{label}<span className="agp-group__count"> · {group.prompts.length}</span></span>
            </h3>
            <span className="agp-group__hint">
                {group.client ? t('agency_prompts.group_client_hint') : t('agency_prompts.group_all_hint')}
            </span>
        </div>
    );
}

/**
 * Los desactivados que el usuario puede devolver (los que creó él: `inactive_own`), plegados al pie.
 * Es la red de «Morten carga de prueba y borra» (README §1.1, caso 2).
 */
function InactiveOwn({prompts, restoringId, onRestore}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    if (!prompts || prompts.length === 0) return null;
    return (
        <div className="agp-inactive">
            <button type="button" className="agp-inactive__toggle" aria-expanded={open}
                    onClick={() => setOpen((value) => !value)}>
                <FaIcon iconName={open ? 'chevron-down' : 'chevron-right'}/>
                {t('agency_prompts.inactive_toggle', {count: prompts.length})}
            </button>
            {open && (
                <div className="agp-rows">
                    {prompts.map((prompt) => (
                        <div key={prompt.id} className="agp-inactive__row">
                            <span className="agp-inactive__name">
                                {prompt.title}
                                {/* Para quién era: dos desactivados con el mismo nombre no se distinguían. */}
                                <span className="agp-inactive__scope">
                                    {prompt.client ? prompt.client.name : t('agency_prompts.group_all_clients')}
                                </span>
                            </span>
                            <button type="button" className="agp-btn -quiet" disabled={restoringId === prompt.id}
                                    onClick={() => onRestore(prompt)}>
                                {t('agency_prompts.restore')}
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * La solapa propia: buscador + filtro de cliente, la lista agrupada por alcance y los desactivados.
 * El estado del buscador y del filtro vive en la página, para que «Adaptado · ver en Propios» pueda
 * limpiarlos antes de resaltar la fila.
 */
export function OwnPromptsTab({
    library, agencyName, search, clientFilter, onSearchChange, onClientFilterChange,
    highlightId, pendingId, restoringId, adaptingId, now,
    onNew, onSeeDashbo, onCopyAsk, onAction, onTogglePortal, onRestore,
    onAdapt, onSeeAdapted, onView, onRate,
}) {
    const {t} = useTranslation();
    const own = library.own || [];
    const clients = sortClients(library.clients);
    // En el filtro, los clientes inactivos sólo si tienen algún prompt: el resto no cambia nada.
    const withPrompts = new Set(own.filter((prompt) => prompt.client).map((prompt) => String(prompt.client.id)));
    const filterClients = clients.filter((client) => client.is_active !== false || withPrompts.has(String(client.id)));
    const groups = groupOwnPrompts(own, library.clients, {search, clientId: clientFilter});
    // Si el cliente filtrado dejó de ser opción (se desactivó el último prompt de un cliente inactivo),
    // el select quedaba en blanco con la lista todavía filtrada por él.
    const filterIsOption = clientFilter === ALL_CLIENTS
        || filterClients.some((client) => String(client.id) === String(clientFilter));
    useEffect(() => {
        if (!filterIsOption) onClientFilterChange(ALL_CLIENTS);
    }, [filterIsOption, onClientFilterChange]);

    if (own.length === 0) {
        const dashbo = library.dashbo || [];
        const suggested = dashbo.filter((prompt) => !prompt.adapted).slice(0, SUGGESTED_COUNT);
        return (
            <>
                <EmptyOwn agencyName={agencyName} suggested={suggested} dashboTotal={dashbo.length}
                          adaptingId={adaptingId} onNew={onNew} onSeeDashbo={onSeeDashbo}
                          onAdapt={onAdapt} onSeeAdapted={onSeeAdapted} onView={onView} onRate={onRate}/>
                <InactiveOwn prompts={library.inactive_own} restoringId={restoringId} onRestore={onRestore}/>
            </>
        );
    }

    return (
        <>
            <div className="agp-tools">
                <label className="agp-search">
                    <FaIcon iconName="search"/>
                    <input type="search" value={search} onChange={(event) => onSearchChange(event.target.value)}
                           placeholder={t('agency_prompts.search_placeholder')}
                           aria-label={t('agency_prompts.search_placeholder')}/>
                </label>
                <select className="agp-select" value={clientFilter}
                        aria-label={t('agency_prompts.filter_client_label')}
                        onChange={(event) => onClientFilterChange(event.target.value)}>
                    <option value={ALL_CLIENTS}>{t('agency_prompts.filter_all_clients')}</option>
                    {filterClients.map((client) => (
                        <option key={client.id} value={String(client.id)}>{clientLabel(t, client)}</option>
                    ))}
                </select>
            </div>

            {groups.length === 0 && <p className="agp-nothing">{t('agency_prompts.no_results')}</p>}

            {groups.map((group) => (
                <section key={group.key} className="agp-group" aria-label={group.client ? group.client.name
                    : t('agency_prompts.group_all_clients')}>
                    <GroupHeading group={group}/>
                    <div className="agp-rows">
                        {group.prompts.map((prompt) => (
                            <PromptRow key={prompt.id} prompt={prompt} now={now}
                                       highlighted={highlightId === prompt.id} pending={pendingId === prompt.id}
                                       onCopyAsk={onCopyAsk} onAction={onAction} onTogglePortal={onTogglePortal}/>
                        ))}
                    </div>
                </section>
            ))}

            <InactiveOwn prompts={library.inactive_own} restoringId={restoringId} onRestore={onRestore}/>
        </>
    );
}
